import { isDefined } from "@clipboard-health/util-ts";
import { differenceInMinutes, hoursToMinutes, parseISO } from "date-fns";

import { type ComputedArrivalData } from "./computeArrivalData";
import { useArrivalTimeConfig } from "./useArrivalTimeConfig";
import { useIsArrivalTimeEligible } from "./useIsArrivalTimeEligible";

interface UseShouldShowArrivalTimeModalProps {
  shiftStartIso: string | undefined;
  workplaceId: string | undefined;
  workplaceMsa: string | undefined;
  computedArrivalData: ComputedArrivalData | undefined;
}

export function useShouldShowArrivalTimeModal(props: UseShouldShowArrivalTimeModalProps) {
  const { shiftStartIso, computedArrivalData, workplaceId, workplaceMsa } = props;

  const { shortLeadTimeOffsetInHours } = useArrivalTimeConfig();

  const isShortLeadTime = useIsArrivalTimeEligible({
    shiftStartIso: shiftStartIso ?? undefined,
    workplaceId: workplaceId ?? "",
    workplaceMsa: workplaceMsa ?? "",
  });

  if (!isDefined(shiftStartIso)) {
    return false;
  }

  const hasComputedArrivalData = isDefined(computedArrivalData);

  const diffMinutes = differenceInMinutes(parseISO(shiftStartIso), new Date());

  // If shift starts within shortLeadTimeOffsetInHours (usually 4 hours) and 24 hours, show arrival time modal
  const isShiftWithinWindowForArrivalTimeModal =
    diffMinutes > hoursToMinutes(shortLeadTimeOffsetInHours) && diffMinutes <= hoursToMinutes(24);

  return (hasComputedArrivalData && isShortLeadTime) || isShiftWithinWindowForArrivalTimeModal;
}
